"use client";

import { useEffect, useRef } from "react";
import { initAuthFromUrl, getAccessToken } from "@/lib/auth";
import { supabase } from "@/lib/supabase";

// Module-level timestamp tracking — survives bfcache freeze
let lastRunTime = 0;

export default function AuthInit() {
  const initRef = useRef(false);

  useEffect(() => {
    // Only run ONCE per page session. In StrictMode dev, the component
    // mounts→unmounts→remounts, but this guard prevents double-init.
    if (initRef.current) return;
    initRef.current = true;

    // Record the time of this fresh mount — set BEFORE async operations
    lastRunTime = Date.now();
    console.log("[AuthInit] FRESH mount at", lastRunTime);

    const run = async () => {
      initAuthFromUrl();

      // Supabase also uses hash for tokens, so check there too
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const hashToken = hashParams.get('token');

      if (!getAccessToken() && hashToken) {
        // Token was in hash - initAuthFromUrl already saved it to localStorage
      }

      if (!getAccessToken()) {
        const { data } = await supabase.auth.getSession();
        if (data.session?.access_token) {
          localStorage.setItem("access_token", data.session.access_token);
          console.log("[AuthInit] Synced Supabase session → localStorage");
        }
        if (data.session?.refresh_token) {
          localStorage.setItem("refresh_token", data.session.refresh_token);
        }
      }

      // Clear hash to remove sensitive token data
      if (window.location.hash.includes('token')) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }

      console.log("[AuthInit] init complete", {
        localStorage_token: localStorage.getItem("access_token") ? "YES" : "NO",
      });

      // Notify AuthContext to re-read token from localStorage
      window.dispatchEvent(new CustomEvent("auth:state-change"));
    };

    run();

    // pageshow fires on back/forward navigation and bfcache restores.
    // For actual bfcache restores (persisted=true), React does NOT re-run
    // useEffect so we must reload to re-hydrate fresh. For regular cross-origin
    // back navigations (persisted=false), React remounts normally — no reload
    // needed as useEffect will run and sync everything correctly.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        console.log("[AuthInit] bfcache restore — forcing reload");
        window.location.reload();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  return null;
}